import { GraphQLResult } from "@aws-amplify/api";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { useCleverlynkContext } from "../contexts/CleverlynkContext";
import { PFXLandingPageResponse } from "../models/Interfaces";
import { selectCompany, selectCurrentClynkWithinGroup } from "../redux/slices/cleverlynkSlice";
import {
  getComparableItemIdInCleverlynk,
  insertNewsletterClient,
  loadLandingPage,
  modifyComponentDimensions,
  modifyLoadingItem,
  selectLandingFromGroup,
} from "../redux/slices/landingSlice";
import { PFXCategoryItem, selectItemsEntities } from "../redux/slices/templateSlice";
import useCleverlynkFunctions from "./useCleverlynkFunctions";
import useTemplateFunctions from "./useTemplateFunctions";

export default function useLandingFunctions() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const landingFromGroup = useSelector(selectLandingFromGroup);
  const chosenClynkWithinGroup = useSelector(selectCurrentClynkWithinGroup);
  const catalogItems = useSelector(selectItemsEntities);
  const company = useSelector(selectCompany);

  const { handleModifyItemToShow } = useTemplateFunctions();
  const { handleModifyOpenGeo, handleModifySnackbar } = useCleverlynkFunctions();

  const cleverlynkContext = useCleverlynkContext();
  const basePath = useMemo(() => cleverlynkContext?.basePath ?? "", [cleverlynkContext]);

  const handleModifyComponentDimensions = (screenWidth: number, screenHeight: number) => {
    let height = screenHeight / 2;
    let width = screenWidth;
    return dispatch(modifyComponentDimensions({ height, width }));
  };

  const handleInsertNewsletterClient = async (email: string) => {
    const s: any = await insertNewsletterClient(email, company?.id ?? "");
    return s?.data?.insertClientForNewsletter;
  };

  const goToItemHelper = (item?: PFXCategoryItem, cat?: string, subcat?: string) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete("landin");
    if (cat) queryParams.append("c", cat);
    if (subcat) queryParams.append("sc", subcat);
    if (item) {
      handleModifyItemToShow(item);
      queryParams.append("it", item?.id ?? "");
    }
    history.push({
      pathname: `${basePath}/menu`.replace("//", "/"),
      search: queryParams.toString(),
    });
  };

  const handleGoToItem = async (item: PFXCategoryItem, cat?: string, subcat?: string) => {
    // getComparableItemIdInCleverlynk({ clynkId: "", item });
    const itemInCurrentCatalog = catalogItems[item?.id!];
    if (!landingFromGroup) {
      goToItemHelper(item, cat, subcat);
    } else if (!!chosenClynkWithinGroup && !!chosenClynkWithinGroup.id) {
      if (!!itemInCurrentCatalog) {
        goToItemHelper(itemInCurrentCatalog, cat, subcat);
        return;
      }
      dispatch(modifyLoadingItem(true));
      const newItemId = await getComparableItemIdInCleverlynk({ clynkId: chosenClynkWithinGroup.id ?? "", item });
      dispatch(modifyLoadingItem(false));
      if (!!newItemId?.id) {
        const newItem = catalogItems[newItemId.id!];
        goToItemHelper(newItem, cat, subcat);
      } else {
        goToItemHelper();
        handleModifySnackbar({ message: "Este producto no se encontró en tu ubicación", type: "warning" });
      }
    } else {
      handleModifyOpenGeo(true);
    }
  };

  const handleGoToLanding = () => {
    history.push({
      pathname: `${basePath}`,
    });
  };

  const handleGoToCoverage = () => {
    history.push({
      pathname: `${basePath}/coverage`.replace("//", "/"),
    });
  };

  const handleGoToCatalog = (replace?: boolean) => {
    if (!!chosenClynkWithinGroup?.id || !landingFromGroup) {
      if (replace) {
        history.replace({
          pathname: `${basePath}/menu`.replace("//", "/"),
          search: location.search ?? "",
        });
      } else {
        history.push({
          pathname: `${basePath}/menu`.replace("//", "/"),
        });
      }
    } else {
      handleModifyOpenGeo(true);
      history.push({
        pathname: `${basePath}/menu`.replace("//", "/"),
      });
    }
  };

  const handleLoadLandingPage = (
    landing: PFXLandingPageResponse,
    groupLandingId: string | undefined,
    setNoLanding: Function
  ) => {
    if (landing) dispatch(loadLandingPage({ ...landing, groupLandingId }));
    else {
      setNoLanding();
      handleGoToCatalog(true);
    }
  };

  return {
    handleModifyComponentDimensions,
    handleLoadLandingPage,
    handleInsertNewsletterClient,
    handleGoToItem,
    handleGoToCatalog,
    handleGoToLanding,
    handleGoToCoverage,
  };
}
